/**
 * src/observability/events.ts
 *
 * Writer canônico de eventos de observabilidade.
 *
 * Regra invariante: nunca lançar exceção. Erros de escrita são logados via
 * console.error e ignorados — o caller não precisa try/catch.
 *
 * Estado atual: stub. _writeToDb apenas faz console.debug. A Task 5 do plano
 * `docs/superpowers/plans/2026-05-08-observabilidade.md` substitui o stub por
 * `database.action()` real após o schema v31 existir.
 */

import { Q } from '@nozbe/watermelondb'
import database from '../database'
import { store } from '../store'

// IDs precisam ser RFC 4122 v4 estritos (o backend valida com regex rígida e
// rejeita com `invalid_id`). `react-uuid` não preenche os bits de versão/variant,
// então usamos `crypto.randomUUID()` (nativo, v4 garantido). Fallback defensivo
// só se algum ambiente exótico não tiver crypto disponível.
function uuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback baseado em getRandomValues — mantém compliance com v4.
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40 // versão 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// ─── Tipos ──────────────────────────────────────────────────────────────────

export type EventLevel = 'info' | 'warn' | 'error'

export type EventCategory =
  | 'sync'
  | 'auth'
  | 'iatf'
  | 'protocolo'
  | 'farm'
  | 'user'
  | 'outro'

export interface EventInput {
  level: EventLevel
  category: EventCategory
  action: string
  payload?: Record<string, unknown>
  userId?: string
  farmId?: string
}

interface InternalEventRecord {
  timestamp: number
  level: EventLevel
  category: EventCategory
  action: string
  payload: string
  userId?: string
  farmId?: string
  deviceId: string
}

// ─── Constantes ─────────────────────────────────────────────────────────────

const PAYLOAD_CAP_BYTES = 4 * 1024 // 4 KB
const EVENTS_MAX = 5000
const EVENTS_TARGET_AFTER_TRIM = 4900
const TRIM_CHECK_PROBABILITY = 1 / 50

// ─── deviceId ───────────────────────────────────────────────────────────────

/**
 * Identificador estável do dispositivo/sessão.
 * Prioridade:
 *   1. window.name — sessionGuid já mantido por verifyDuplicatePages.js
 *   2. localStorage.deviceId — persistido entre sessões
 *   3. Hash leve de navigator.userAgent + Date.now() — gerado uma vez e persistido
 */
function getDeviceId(): string {
  try {
    if (typeof window !== 'undefined' && window.name && window.name.length > 0) {
      return window.name
    }

    const stored = localStorage.getItem('deviceId')
    if (stored) return stored

    const raw = (navigator.userAgent || '') + String(Date.now())
    let hash = 0
    for (let i = 0; i < raw.length; i++) {
      const ch = raw.charCodeAt(i)
      hash = ((hash << 5) - hash) + ch
      hash = hash & hash
    }
    const deviceId = 'dev_' + Math.abs(hash).toString(36)
    localStorage.setItem('deviceId', deviceId)
    return deviceId
  } catch {
    return 'unknown'
  }
}

// ─── Captura de contexto do Redux ───────────────────────────────────────────

function getContextFromStore(): { userId?: string; farmId?: string } {
  try {
    const state: any = store.getState()
    return {
      userId: state?.auth?.logged?.userId ?? undefined,
      farmId: state?.farm?.selected?.id ?? undefined,
    }
  } catch {
    return {}
  }
}

// ─── Serialização do payload com cap de 4 KB ────────────────────────────────

function serializePayload(payload?: Record<string, unknown>): string {
  if (!payload) return ''
  try {
    const serialized = JSON.stringify(payload)
    const size = typeof Blob !== 'undefined'
      ? new Blob([serialized]).size
      : serialized.length
    if (size <= PAYLOAD_CAP_BYTES) return serialized
    return JSON.stringify({ _truncated: true, keys: Object.keys(payload) })
  } catch {
    return '{"_serializationError":true}'
  }
}

// ─── Retenção ───────────────────────────────────────────────────────────────

/**
 * Cap por quantidade. Verificação probabilística (1/50) para não pagar
 * `fetchCount` em cada escrita. Quando excede `EVENTS_MAX`, deleta os mais
 * antigos por `created_at` ASC até voltar a `EVENTS_TARGET_AFTER_TRIM`.
 */
async function trimEventsIfNeeded(): Promise<void> {
  if (Math.random() > TRIM_CHECK_PROBABILITY) return

  try {
    await database.action(async () => {
      const collection: any = database.collections.get('events')
      const count: number = await collection.query().fetchCount()
      if (count <= EVENTS_MAX) return

      const toDelete = count - EVENTS_TARGET_AFTER_TRIM
      const oldest: any[] = await collection
        .query(
          Q.experimentalSortBy('created_at', Q.asc),
          Q.experimentalTake(toDelete),
        )
        .fetch()

      await database.batch(
        ...oldest.map((r) => r.prepareDestroyPermanently()),
      )
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[events.trimEventsIfNeeded] failed', err)
  }
}

// ─── Writer interno ──────────────────────────────────────────────────────────

async function _writeToDb(record: InternalEventRecord): Promise<void> {
  await database.action(async () => {
    const collection: any = database.collections.get('events')
    await collection.create((entity: any) => {
      entity._raw.id = uuidV4()
      entity._raw.created_at = record.timestamp
      entity._raw.updated_at = record.timestamp
      entity.timestamp = record.timestamp
      entity.level = record.level
      entity.category = record.category
      entity.action = record.action
      entity.payload = record.payload
      entity.userId = record.userId ?? null
      entity.farmId = record.farmId ?? null
      entity.deviceId = record.deviceId
      entity.isDeleted = false
      entity.priority = false
    })
  })

  // Trim assíncrono — não bloqueia o caller. Erros já são logados internamente.
  void trimEventsIfNeeded()
}

// ─── Push imediato pra eventos de erro ──────────────────────────────────────

/**
 * Eventos `level: 'error'` precisam chegar no backend antes do usuário fechar
 * o tab. O ciclo normal do sync espera até 30s — tarde demais pra debug.
 *
 * - Debounce de 250ms coalesce rajadas (e.g., render loop emitindo N erros) em
 *   uma única chamada de pushEvents.
 * - Flag `pushScheduled` evita kicks concorrentes enquanto uma já está pending.
 * - Dynamic import quebra o ciclo events.ts ↔ syncWorker.js (syncWorker importa
 *   recordEvent estaticamente).
 * - Falhas são silenciadas — gate do pushEvents (REACT_APP_TOGGLE_EVENTS_API),
 *   token ausente, ou erro de rede caem aqui sem propagar.
 */
let pushScheduled = false

async function triggerImmediatePush(): Promise<void> {
  if (pushScheduled) return
  pushScheduled = true
  try {
    await new Promise((resolve) => setTimeout(resolve, 250))
    const [syncMod, authMod] = await Promise.all([
      import('../syncWorker'),
      import('../redux/auth/actions'),
    ])
    const auth = await authMod.getLoggedUser()
    if (!auth?.token) return
    await syncMod.pushEvents(auth.token)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[events.triggerImmediatePush] failed', err)
  } finally {
    pushScheduled = false
  }
}

// ─── API pública ────────────────────────────────────────────────────────────

export async function recordEvent(input: EventInput): Promise<void> {
  try {
    const ctx = getContextFromStore()
    await _writeToDb({
      timestamp: Date.now(),
      level: input.level,
      category: input.category,
      action: input.action,
      payload: serializePayload(input.payload),
      userId: input.userId ?? ctx.userId,
      farmId: input.farmId ?? ctx.farmId,
      deviceId: getDeviceId(),
    })

    // Eventos de erro disparam push imediato (não bloqueia o caller).
    if (input.level === 'error') {
      void triggerImmediatePush()
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[events.recordEvent] failed', err)
  }
}

export async function recordError(
  error: unknown,
  ctx: Omit<EventInput, 'level'>,
): Promise<void> {
  try {
    const err =
      error instanceof Error ? error : new Error(String(error ?? 'unknown'))
    await recordEvent({
      ...ctx,
      level: 'error',
      payload: {
        ...(ctx.payload ?? {}),
        message: err.message,
        stack: typeof err.stack === 'string' ? err.stack.slice(0, 500) : '',
      },
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[events.recordError] failed', e)
  }
}

export async function recordSaveOutcome(
  category: EventCategory,
  entityName: string,
  outcome: 'success' | 'error',
  extra?: Record<string, unknown>,
): Promise<void> {
  try {
    await recordEvent({
      level: outcome === 'success' ? 'info' : 'error',
      category,
      action: `${entityName}_${outcome === 'success' ? 'saved' : 'save_failed'}`,
      payload: extra,
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[events.recordSaveOutcome] failed', e)
  }
}
